<template>
  <div class="support-page">
    <div class="support-shell">
      <div class="support-brand">
        <img class="support-logo" :src="logoUrl" alt="Prestalight" />
      </div>

      <header class="support-hero">
        <p class="support-eyebrow">ATENCIÓN AL CLIENTE</p>
        <h1 class="support-title">Estamos aquí para ayudarte</h1>
        <p class="support-description">
          Si tienes alguna duda o necesitas ayuda, contáctanos por el canal que
          prefieras. Estamos listos para echarte una mano.
        </p>
      </header>

      <section class="support-panel">
        <div class="support-grid">
          <a
            v-for="item in contacts"
            :key="item.title"
            class="support-card"
            :href="item.href"
            :target="item.external ? '_blank' : undefined"
            rel="noreferrer"
          >
            <div class="support-icon">
              <van-icon :name="item.icon" />
            </div>
            <div class="support-card-title">{{ item.title }}</div>
            <div class="support-card-value">{{ item.value }}</div>
            <div class="support-card-description">{{ item.description }}</div>
          </a>
        </div>
      </section>

      <section class="support-legal">
        <router-link class="support-card support-card-legal" to="/privacy_policy/ios">
          <div class="support-card-legal-main">
            <div class="support-icon support-card-legal-icon">
              <van-icon name="shield-o" />
            </div>

            <div class="support-card-legal-copy">
              <div class="support-card-title support-card-legal-title">
                Política de privacidad
              </div>
              <div class="support-card-description support-card-legal-description">
                Consulta nuestra política de privacidad y conoce cómo protegemos
                tus datos personales.
              </div>
            </div>
          </div>

          <div class="support-card-legal-action">
            <span class="support-card-legal-action-text">Ver acuerdo</span>
            <img
              class="support-card-legal-arrow"
              :src="require('@/assets/image/ic_arrow.png')"
              alt=""
            />
          </div>
        </router-link>
      </section>

      <section class="support-story">
        <header class="story-intro">
          <h2 class="story-heading">
            More Than Credit: Our Mission, Vision &amp; Purpose
          </h2>
          <p class="story-lead">
            At Prestalight, credit is not a burden but a source of opportunity.
          </p>
          <p class="story-text">
            We strive to make finance accessible and fair, guided by
            transparency and empathy, with the ambition to set a new standard
            in Spain's microcredit market. Above all, we want credit to be
            positive, human, and a real ally in improving everyday life.
          </p>
        </header>

        <div class="story-showcase">
          <div class="story-copy">
            <h3 class="story-title">Credit that empowers.</h3>
            <p class="story-description">
              Prestalight supports life's key moments, helping you face the
              unexpected, achieve your goals, and live with confidence. Borrow
              up to €2,000, with clarity, speed, and care.
            </p>
          </div>

          <div class="story-device">
            <img class="story-device-image" :src="mobileUrl" alt="Prestalight app" />
          </div>

          <div class="story-wave" aria-hidden="true"></div>
          <div class="story-loop" aria-hidden="true"></div>

          <div class="story-badge">
            <span class="story-badge-label">Hasta</span>
            <span class="story-badge-value">2.000 €</span>
          </div>
        </div>
      </section>

      <div class="support-footer">
        <h3>Nébula Finance, S.A.© 2026 All Rights Reserved</h3>
      </div>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted } from 'vue';
import logoUrl from '@/assets/image/logo.svg';
import mobileUrl from '@/assets/image/mobile.svg';

const contacts = [
  {
    title: 'WhatsApp',
    value: '692650021',
    description: 'Dudas rápidas, respuestas rápidas.',
    icon: 'chat-o',
    href: 'https://wa.me/34692650021',
    external: true
  },
  {
    title: 'Por teléfono',
    value: '918055475',
    description: 'de lunes a sábado, de 9:00 a 21:00.',
    icon: 'phone-o',
    href: 'tel:918055475',
    external: false
  },
  {
    title: 'Por email',
    value: 'clientes@prestalight.com',
    description: 'Escríbenos y te contestamos cuanto antes.',
    icon: 'envelop-o',
    href: 'mailto:clientes@prestalight.com',
    external: false
  }
];

export default defineComponent({
  name: 'supportPage',
  setup() {
    onMounted(() => {
      document.title = 'Atención al cliente';
    });

    return {
      contacts,
      logoUrl,
      mobileUrl
    };
  }
});
</script>

<style lang="scss" scoped>
.support-page {
  width: 100vw;
  min-height: 100vh;
  margin-left: calc(50% - 50vw);
  padding: 72px 24px 48px;
  box-sizing: border-box;
  background:
    radial-gradient(circle at top center, rgba(133, 204, 255, 0.2), transparent 34%),
    radial-gradient(circle at 100% 0%, rgba(193, 228, 255, 0.4), transparent 28%),
    linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%);
}

.support-shell {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
}

.support-brand {
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
}

.support-logo {
  display: block;
  width: auto;
  max-width: 420px;
  height: 104px;
}

.support-hero {
  max-width: 760px;
  margin: 0 auto 44px;
  text-align: center;
}

.support-eyebrow {
  margin-bottom: 16px;
  color: #5f6168;
  font-family: Manrope-Bold, serif;
  font-size: 18px;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.support-title {
  margin-bottom: 20px;
  color: #13151a;
  font-family: Fredoka-Medium, serif;
  font-size: 54px;
  line-height: 1.12;
}

.support-description {
  margin: 0 auto;
  max-width: 620px;
  color: #666c76;
  font-family: Manrope-Medium, serif;
  font-size: 26px;
  line-height: 1.62;
}

.support-panel {
  padding: 12px;
  border-radius: 36px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.75) 0%, rgba(233, 242, 255, 0.9) 100%);
  box-shadow: 0 24px 80px rgba(97, 126, 153, 0.1);
  backdrop-filter: blur(12px);
}

.support-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

.support-card {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 240px;
  padding: 30px;
  border-radius: 28px;
  box-sizing: border-box;
  text-align: left;
  text-decoration: none;
  background:
    radial-gradient(circle at 20% 15%, rgba(142, 206, 255, 0.28), transparent 18%),
    radial-gradient(circle at 78% 82%, rgba(198, 224, 255, 0.35), transparent 28%),
    rgba(255, 255, 255, 0.88);
  box-shadow: 0 20px 50px rgba(112, 144, 176, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.8);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.support-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 24px 52px rgba(112, 144, 176, 0.18);
}

.support-card:active {
  transform: scale(0.985);
}

.support-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 36px;
  border-radius: 18px;
  background: linear-gradient(180deg, #84d4ff 0%, #7ab9ff 100%);
  color: #103b63;
  font-size: 30px;
  box-shadow: 0 12px 24px rgba(100, 174, 255, 0.28);
}

.support-card-title {
  margin-bottom: 18px;
  color: #1d1f24;
  font-family: Fredoka-Medium, serif;
  font-size: 30px;
  line-height: 1.25;
}

.support-card-value {
  margin-bottom: 14px;
  color: #111318;
  font-family: Manrope-Bold, serif;
  font-size: 38px;
  line-height: 1.2;
  word-break: break-word;
}

.support-card-description {
  color: #6d737d;
  font-family: Manrope-Medium, serif;
  font-size: 24px;
  line-height: 1.5;
}

.support-story {
  margin-top: 56px;
}

.support-footer {
  margin-top: 24px;
}

.support-legal {
  margin-top: 22px;
}

.support-card-legal {
  display: flex;
  flex-direction: column;
  gap: 22px;
  min-height: auto;
}

.support-card-legal-main {
  display: flex;
  align-items: flex-start;
  gap: 18px;
}

.support-card-legal-icon {
  margin-bottom: 0;
}

.support-card-legal-copy {
  flex: 1;
}

.support-card-legal-title {
  margin-bottom: 10px;
}

.support-card-legal-description {
  max-width: 840px;
}

.support-card-legal-action {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #111318;
  font-family: Manrope-Bold, serif;
  font-size: 24px;
  line-height: 1;
}

.support-card-legal-action-text {
  display: inline-block;
}

.support-card-legal-arrow {
  display: block;
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.story-intro {
  max-width: 980px;
  margin: 0 auto 32px;
  text-align: center;
}

.story-heading {
  margin-bottom: 20px;
  color: #111318;
  font-family: Fredoka-Medium, serif;
  font-size: 42px;
  line-height: 1.22;
}

.story-lead,
.story-text {
  color: #343842;
  font-family: Manrope-Medium, serif;
  font-size: 24px;
  line-height: 1.7;
}

.story-lead {
  margin-bottom: 12px;
}

.story-showcase {
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;
  align-items: center;
  padding: 40px 28px 132px;
  border-radius: 36px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.92) 0%, rgba(244, 248, 255, 0.98) 100%);
  box-shadow: 0 26px 80px rgba(93, 121, 149, 0.1);
}

.story-copy {
  position: relative;
  z-index: 2;
  max-width: 540px;
}

.story-title {
  margin-bottom: 20px;
  color: #111318;
  font-family: Fredoka-Medium, serif;
  font-size: 56px;
  line-height: 1.1;
}

.story-description {
  color: #20242b;
  font-family: Manrope-Bold, serif;
  font-size: 28px;
  line-height: 1.6;
}

.story-device {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
}

.story-device-image {
  display: block;
  width: 100%;
  max-width: 520px;
  height: auto;
}

.story-wave {
  position: absolute;
  left: -8%;
  bottom: 26px;
  width: 94%;
  height: 170px;
  border-top: 12px solid #99e7ff;
  border-radius: 50% 50% 0 0 / 100% 100% 0 0;
  opacity: 0.95;
}

.story-loop {
  position: absolute;
  right: 64px;
  bottom: 74px;
  width: 116px;
  height: 88px;
  border: 12px solid #8dcffd;
  border-left-color: transparent;
  border-bottom-color: transparent;
  border-radius: 64% 52% 62% 58%;
  transform: rotate(18deg);
  opacity: 0.95;
}

.story-badge {
  position: absolute;
  right: 28px;
  bottom: 18px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 12px;
}

.story-badge-label {
  color: #111318;
  font-family: Fredoka-Medium, serif;
  font-size: 36px;
  line-height: 1;
}

.story-badge-value {
  display: inline-flex;
  align-items: center;
  min-height: 64px;
  padding: 0 18px;
  background: #0f1115;
  color: #ffffff;
  font-family: Manrope-Bold, serif;
  font-size: 34px;
  line-height: 1;
}

@media (min-width: 768px) {
  .support-page {
    padding: 88px 36px 64px;
  }

  .support-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .story-showcase {
    padding: 46px 36px 132px;
  }
}

@media (min-width: 1100px) {
  .support-page {
    padding: 96px 48px 72px;
  }

  .support-brand {
    margin-bottom: 48px;
  }

  .support-logo {
    max-width: 520px;
    height: 128px;
  }

  .support-hero {
    margin-bottom: 56px;
  }

  .support-title {
    font-size: 60px;
  }

  .support-description {
    font-size: 24px;
  }

  .support-panel {
    padding: 18px;
    border-radius: 40px;
  }

  .support-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px;
  }

  .support-card {
    min-height: 292px;
    padding: 34px;
  }

  .support-icon {
    width: 62px;
    height: 62px;
    font-size: 32px;
  }

  .support-card-title {
    font-size: 28px;
  }

  .support-card-value {
    font-size: 34px;
  }

  .support-card-description {
    font-size: 22px;
  }

  .support-story {
    margin-top: 72px;
  }

  .support-legal {
    margin-top: 24px;
  }

  .support-card-legal {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 30px 34px;
  }

  .support-card-legal-main {
    align-items: center;
  }

  .support-card-legal-title {
    font-size: 28px;
  }

  .support-card-legal-description {
    max-width: 700px;
    font-size: 19px;
  }

  .support-card-legal-action {
    flex-shrink: 0;
    font-size: 20px;
  }

  .support-card-legal-arrow {
    width: 20px;
    height: 20px;
  }

  .story-intro {
    margin-bottom: 40px;
  }

  .story-heading {
    font-size: 46px;
  }

  .story-lead,
  .story-text {
    font-size: 18px;
    line-height: 1.75;
  }

  .story-showcase {
    grid-template-columns: minmax(0, 0.95fr) minmax(420px, 0.9fr);
    gap: 18px;
    min-height: 520px;
    padding: 42px 34px 88px 42px;
  }

  .story-title {
    font-size: 58px;
  }

  .story-description {
    max-width: 560px;
    font-size: 24px;
  }

  .story-device {
    justify-content: flex-end;
    padding-right: 24px;
  }

  .story-device-image {
    max-width: 540px;
  }

  .story-wave {
    left: -2%;
    bottom: -8px;
    width: 88%;
    height: 168px;
    border-top-width: 10px;
  }

  .story-loop {
    right: 72px;
    bottom: 104px;
    width: 124px;
    height: 92px;
    border-width: 10px;
  }

  .story-badge {
    right: 34px;
    bottom: 18px;
  }

  .story-badge-label {
    font-size: 34px;
  }

  .story-badge-value {
    min-height: 58px;
    padding: 0 16px;
    font-size: 28px;
  }
}

@media (max-width: 767px) {
  .support-legal {
    margin-top: 18px;
  }

  .support-card-legal-action {
    font-size: 22px;
  }

  .support-card-legal-arrow {
    width: 22px;
    height: 22px;
  }

  .story-heading {
    font-size: 34px;
  }

  .story-lead,
  .story-text {
    font-size: 22px;
  }

  .story-title {
    font-size: 44px;
  }

  .story-description {
    font-size: 24px;
  }

  .story-device-image {
    max-width: 420px;
  }

  .story-wave {
    left: -18%;
    bottom: 74px;
    width: 120%;
    height: 116px;
    border-top-width: 10px;
  }

  .story-loop {
    right: 20px;
    bottom: 122px;
    width: 88px;
    height: 66px;
    border-width: 10px;
  }

  .story-badge {
    right: 20px;
    bottom: 22px;
    gap: 10px;
  }

  .story-badge-label {
    font-size: 28px;
  }

  .story-badge-value {
    min-height: 54px;
    padding: 0 14px;
    font-size: 26px;
  }
}
</style>
